import { useTranslation } from 'react-i18next';

import { useCsvFields } from '../hooks/useCsvFields';

import { Badge } from '@/components/ui/Badge';
import {
  Card,
  CardTitle,
  CardContent,
  CardDescription,
  CardHeader,
} from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { CSVType } from '@/services/CSVService';

interface TemplateFieldsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sheetType: CSVType;
}

export function TemplateFieldsModal({
  open,
  onOpenChange,
  sheetType,
}: TemplateFieldsModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ProductsManager',
  });

  const { fields } = useCsvFields(sheetType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-3xl space-y-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            {t('templateFieldsModal.title')} -{' '}
            {sheetType === CSVType.PRODUCT ? t('products') : t('shopTheLook')}
          </DialogTitle>
          <DialogDescription>
            {t('templateFieldsModal.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {fields.map((field) => (
            <Card key={field.name}>
              <CardContent className="space-y-2 p-4">
                <CardHeader className="p-0">
                  <CardTitle className="flex flex-row gap-2">
                    <code className="rounded bg-muted text-sm font-semibold">
                      {field.name}
                    </code>
                    {field.required && (
                      <Badge className="rounded bg-black text-xs font-medium text-white">
                        {t('templateFieldsModal.requiredBadge')}
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {field.type}
                    </span>
                  </CardTitle>

                  <CardDescription>{field.description}</CardDescription>
                </CardHeader>

                <div className="rounded bg-muted">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('templateFieldsModal.exampleLabel')}
                  </p>
                  <code className="text-sm">{field.example}</code>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
